// src/features/auth/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, User } from "./types";
import { loginUserAPI, registerUserAPI, googleAuthAPI, forgotPasswordAPI, resetPasswordAPI, sendOTPAPI, verifyOTPAPI, resendOTPAPI, updateAvatarAPI, sendEmailCodeAPI, verifyEmailCodeAPI } from "./authAPI";

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// LOGIN thunk
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const user = await loginUserAPI(credentials);
      return user;
    } catch (err: any) {
      // Pass full error data for special handling (e.g., ACCOUNT_DEACTIVATED)
      const errorData = err.response?.data;
      if (errorData?.code) {
        return rejectWithValue({
          code: errorData.code,
          message: errorData.message ?? "Login failed",
          canRecover: errorData.canRecover ?? errorData.code === 'ACCOUNT_DEACTIVATED',
          identifier: credentials.email,
        });
      }
      return rejectWithValue({
        message: errorData?.message ?? err.message ?? "Login failed",
      });
    }
  }
);

// SEND EMAIL VERIFICATION CODE thunk
export const sendEmailCode = createAsyncThunk(
  "auth/sendEmailCode",
  async (data: { name: string; email: string }, { rejectWithValue }) => {
    try {
      return await sendEmailCodeAPI(data);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.code) {
        return rejectWithValue({
          code: errorData.code,
          message: errorData.message ?? "Failed to send code",
        });
      }
      return rejectWithValue({ message: errorData?.message ?? err.message ?? "Failed to send code" });
    }
  }
);

// VERIFY EMAIL CODE thunk
export const verifyEmailCode = createAsyncThunk(
  "auth/verifyEmailCode",
  async (data: { email: string; code: string }, { rejectWithValue }) => {
    try {
      await verifyEmailCodeAPI(data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Invalid verification code");
    }
  }
);

// REGISTER thunk (auto-login with returned token)
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (
    data: { name: string; email: string; phone: string; password: string },
    { rejectWithValue }
  ) => {
    try {
      const user = await registerUserAPI(data);
      return user;
    } catch (err: any) {
      // Pass full error data for special handling (e.g., ACCOUNT_DEACTIVATED)
      const errorData = err.response?.data;
      if (errorData?.code) {
        return rejectWithValue({
          code: errorData.code,
          message: errorData.message ?? "Signup failed",
          canRecover: errorData.canRecover,
          identifier: data.email || data.phone,
        });
      }
      return rejectWithValue({
        message: errorData?.message ?? err.message ?? "Signup failed",
      });
    }
  }
);

// GOOGLE AUTH thunk
export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async (credential: string, { rejectWithValue }) => {
    try {
      const user = await googleAuthAPI(credential);
      return user;
    } catch (err: any) {
      const errorData = err.response?.data;
      return rejectWithValue({
        code: errorData?.code,
        message: errorData?.message ?? err.message ?? "Google authentication failed",
        canRecover: errorData?.canRecover,
        identifier: errorData?.identifier,
      });
    }
  }
);

// FORGOT PASSWORD thunk
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async (email: string, { rejectWithValue }) => {
    try {
      const message = await forgotPasswordAPI(email);
      return message;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Failed to send reset email");
    }
  }
);

// RESET PASSWORD thunk
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, password }: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const user = await resetPasswordAPI(token, password);
      return user;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Failed to reset password");
    }
  }
);

// SEND OTP thunk
export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await sendOTPAPI(phone);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Failed to send OTP");
    }
  }
);

// VERIFY OTP thunk
export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async ({ phone, otp, name }: { phone: string; otp: string; name?: string }, { rejectWithValue }) => {
    try {
      const result = await verifyOTPAPI(phone, otp, name);
      
      if ('requiresName' in result && result.requiresName) {
        return { requiresName: true as const };
      }
      
      return result;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "OTP verification failed");
    }
  }
);

// RESEND OTP thunk
export const resendOTP = createAsyncThunk(
  "auth/resendOTP",
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await resendOTPAPI(phone);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Failed to resend OTP");
    }
  }
);

// UPDATE AVATAR thunk
export const updateAvatar = createAsyncThunk(
  "auth/updateAvatar",
  async (avatar: string, { rejectWithValue }) => {
    try {
      const user = await updateAvatarAPI(avatar);
      return user;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? err.message ?? "Failed to update avatar");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    },
    // Optional: setUser for hydrating from persisted store
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    // Initialize auth from localStorage
    initializeAuth(state) {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          state.user = { ...user, token };
          state.isAuthenticated = true;
        } catch (error) {
          console.error("Failed to parse user from localStorage", error);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // SEND EMAIL CODE
      .addCase(sendEmailCode.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(sendEmailCode.fulfilled, (state) => { state.loading = false; })
      .addCase(sendEmailCode.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as { code?: string; message?: string } | string;
        state.error = typeof payload === 'string' ? payload : (payload?.message ?? 'Failed to send code');
      })

      // VERIFY EMAIL CODE
      .addCase(verifyEmailCode.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(verifyEmailCode.fulfilled, (state) => { state.loading = false; })
      .addCase(verifyEmailCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // LOGIN
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        // Extract message string from error object to avoid rendering object as React child
        const payload = action.payload as { code?: string; message?: string } | string;
        state.error = typeof payload === 'string' ? payload : (payload?.message ?? 'Login failed');
      })

      // REGISTER (auto login)
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        // Extract message string from error object to avoid rendering object as React child
        const payload = action.payload as { code?: string; message?: string } | string;
        state.error = typeof payload === 'string' ? payload : (payload?.message ?? 'Signup failed');
      })

      // GOOGLE AUTH
      .addCase(googleAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleAuth.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as { message?: string } | string;
        state.error = typeof payload === 'string' ? payload : (payload?.message || 'Google authentication failed');
      })

      // FORGOT PASSWORD
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // RESET PASSWORD (auto login after reset)
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem("token", action.payload.token);
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // SEND OTP
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // VERIFY OTP (auto login on success)
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        // Check if it's a requiresName response
        if ('requiresName' in action.payload && action.payload.requiresName) {
          // Don't login, just clear loading
          return;
        }
        // It's a successful login response
        const { user } = action.payload as { user: User; isNewUser: boolean };
        state.user = user;
        state.isAuthenticated = true;
        localStorage.setItem("token", user.token);
        localStorage.setItem("user", JSON.stringify(user));
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // RESEND OTP
      .addCase(resendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOTP.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // UPDATE AVATAR
      .addCase(updateAvatar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAvatar.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(updateAvatar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, setUser, initializeAuth } = authSlice.actions;
export default authSlice.reducer;
